import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Plus, PieChart, RefreshCw, Download, Upload, GripVertical } from 'lucide-react';
import { watchlistService } from '../services/watchlistService';
import { useLanguage } from '../contexts/LanguageContext';

const Portfolio = ({ onStockSelect, onWatchlistChange }) => {
  const { t } = useLanguage();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const fileInputRef = useRef(null);

  const loadWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await watchlistService.getWatchlist();
      setWatchlist(data);
      if (onWatchlistChange) {
        onWatchlistChange(data);
      }
    } catch (err) {
      setError('Failed to load watchlist');
      console.error('Error loading watchlist:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWatchlist();
  }, []);


  const removeFromWatchlist = async (symbol) => {
    try {
      await watchlistService.removeFromWatchlist(symbol);
      const newWatchlist = watchlist.filter(item => item.symbol !== symbol);
      setWatchlist(newWatchlist);
      if (onWatchlistChange) {
        onWatchlistChange(newWatchlist);
      }
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      setError('Failed to remove from watchlist');
    }
  };

  const handleExport = async () => {
    try {
      setError(null);
      const exportData = await watchlistService.exportWatchlist();
      
      // Create and download file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `watchlist_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setImportMessage('Watchlist exported successfully!');
      setTimeout(() => setImportMessage(''), 3000);
    } catch (err) {
      console.error('Error exporting watchlist:', err);
      setError('Failed to export watchlist');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImportLoading(true);
      setError(null);
      setImportMessage('');

      const text = await file.text();
      const importData = JSON.parse(text);
      
      const result = await watchlistService.importWatchlist(importData);
      
      if (result.success) {
        setImportMessage(result.message);
        await loadWatchlist(); // Refresh the watchlist
        
        if (window.refreshWatchlist) {
          window.refreshWatchlist();
        }
      } else {
        setError('Import failed');
      }
    } catch (err) {
      console.error('Error importing watchlist:', err);
      if (err.message.includes('JSON')) {
        setError('Invalid file format. Please select a valid watchlist JSON file.');
      } else {
        setError('Failed to import watchlist: ' + err.message);
      }
    } finally {
      setImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Clear message after 5 seconds
      setTimeout(() => setImportMessage(''), 5000);
    }
  };

  const handleStockClick = (stock) => {
    onStockSelect(stock);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ''); // Required for Firefox
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    // Only clear dragOverIndex if we're leaving the item entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    try {
      setIsReordering(true);
      setError(null);

      // Create new array with reordered items
      const newWatchlist = [...watchlist];
      const draggedItem = newWatchlist[draggedIndex];
      
      // Remove dragged item
      newWatchlist.splice(draggedIndex, 1);
      
      // Insert at new position
      newWatchlist.splice(dropIndex, 0, draggedItem);
      
      // Update local state immediately for responsiveness
      setWatchlist(newWatchlist);
      
      // Send new order to backend
      const orderedSymbols = newWatchlist.map(item => item.symbol);
      await watchlistService.reorderWatchlist(orderedSymbols);
      
      setImportMessage('Watchlist reordered successfully!');
      setTimeout(() => setImportMessage(''), 2000);
      
    } catch (err) {
      console.error('Error reordering watchlist:', err);
      setError('Failed to reorder watchlist');
      // Reload to get correct order from server
      await loadWatchlist();
    } finally {
      setIsReordering(false);
      setDraggedIndex(null);
      setDragOverIndex(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Expose loadWatchlist function globally for other components to refresh
  useEffect(() => {
    window.refreshWatchlist = loadWatchlist;
  }, []);

  return (
    <div className="card">
      <div className="portfolio-header">
        <h3 className="portfolio-title">
          <PieChart size={20} />
          {t('watchlist')}
        </h3>
        <div className="portfolio-header-actions">
          <span className="portfolio-count">
            {watchlist.length} {t('watchlistCount')}{isReordering && ` (${t('dragToReorder')}...)`}
          </span>
          <div className="portfolio-action-buttons">
            <button
              onClick={handleExport}
              className="export-btn"
              title={t('exportWatchlist')}
              disabled={watchlist.length === 0}
            >
              <Download size={16} />
            </button>
            <button
              onClick={handleImportClick}
              className="import-btn"
              title={t('importWatchlist')}
              disabled={importLoading}
            >
              <Upload size={16} className={importLoading ? 'spinning' : ''} />
            </button>
            <button
              onClick={loadWatchlist}
              className="refresh-watchlist-btn"
              disabled={loading}
              title={t('refresh')}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".json"
        style={{ display: 'none' }}
      />

      {importMessage && (
        <div className="success-message">
          <span>{importMessage}</span>
          <button onClick={() => setImportMessage('')} className="dismiss-success">×</button>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="dismiss-error">×</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <RefreshCw className="loading-icon spinning" />
          <p>{t('loading')}</p>
        </div>
      ) : watchlist.length === 0 ? (
        <div className="empty-state">
          <Plus className="empty-state-icon" />
          <p>{t('emptyWatchlist')}</p>
          <p style={{fontSize: '0.875rem', marginTop: '0.5rem'}}>
            {t('addFirstStock')}
          </p>
        </div>
      ) : (
        <div className="watchlist-items">
          {watchlist.map((stock, index) => (
            <div
              key={stock.symbol}
              className={`watchlist-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              onClick={() => handleStockClick(stock)}
            >
              <div className="drag-handle">
                <GripVertical size={16} />
              </div>
              <div className="watchlist-item-info">
                <div className="watchlist-item-header">
                  <span className="watchlist-item-symbol">
                    {stock.symbol}
                  </span>
                  <span className="watchlist-item-region">
                    {stock.region}
                  </span>
                </div>
                <div className="watchlist-item-name">
                  {stock.name}
                </div>
                <div className="watchlist-item-meta">
                  {stock.type} • {stock.currency}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFromWatchlist(stock.symbol);
                }}
                className="remove-btn"
                title="Remove from watchlist"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="portfolio-tips">
        <h4>Quick Tips</h4>
        <ul>
          <li>• Search for stocks to add them to your watchlist</li>
          <li>• Click on any stock to view its details and charts</li>
          <li>• Your watchlist is saved to your account</li>
        </ul>
      </div>
    </div>
  );
};

export default Portfolio;